/**
 * CR-6243: one branding image upload tile, with a preview of what is applied.
 *
 * The four tiles on the Branding tab used to render a dashed box and an upload
 * arrow forever — identical whether or not an image was stored — so an admin
 * had no way to tell an applied logo from an empty slot, and no confirmation
 * that an upload had landed. This shows the stored image when there is one,
 * the placeholder when there is not, and a spinner while a file is in flight.
 *
 * @link BrandingTab for the four tiles
 * @link useAdminSettings.handleFileUpload for the upload + verification path
 */
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Loader2, Upload } from 'lucide-react';

export interface BrandingImageTileProps {
  /** Tile key the upload handler speaks, e.g. 'companyLogo'. */
  fileType: string;
  /** Visible field name. */
  label: string;
  /** One line telling the admin what this image is used for. */
  hint: string;
  /** Stored image URL, or null when nothing is applied yet. */
  imageUrl: string | null;
  /** True while this tile's file is uploading. */
  uploading: boolean;
  /** Opens the file picker for this tile. */
  onUpload: (fileType: string) => void;
  /**
   * Checkerboard behind the preview. Logos are usually transparent PNGs, so a
   * plain white swatch hides a white logo entirely; the login background is a
   * full-bleed photo and needs no such help.
   */
  transparent?: boolean;
}

export function BrandingImageTile({
  fileType,
  label,
  hint,
  imageUrl,
  uploading,
  onUpload,
  transparent = true,
}: BrandingImageTileProps) {
  const checker =
    'repeating-conic-gradient(var(--muted, #e5e7eb) 0% 25%, transparent 0% 50%) 50% / 16px 16px';

  return (
    // The label names the tile, not a single control — pointing htmlFor at the
    // button would make "Company Light Logo" the button's accessible name and
    // hide what the button actually does. Group + aria-labelledby instead.
    <div className="space-y-2" role="group" aria-labelledby={`${fileType}-label`}>
      <Label id={`${fileType}-label`}>{label}</Label>
      <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary transition-colors">
        <div
          className="h-28 mb-3 rounded-md flex items-center justify-center overflow-hidden bg-muted/30"
          style={imageUrl && transparent ? { background: checker } : undefined}
        >
          {uploading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={`${label} currently applied`}
              className="max-h-28 max-w-full object-contain"
            />
          ) : (
            <Upload className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          {uploading ? 'Uploading…' : imageUrl ? hint : `No image applied — ${hint.toLowerCase()}`}
        </p>
        <Button
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => onUpload(fileType)}
        >
          {imageUrl ? 'Replace' : 'Choose File'}
        </Button>
      </div>
    </div>
  );
}
